import axios from '../config/axiosConfig';

const API_URL = '/remediation';

// Get all remediations with filters
const getRemediations = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.techniqueId) params.append('techniqueId', filters.techniqueId);
  if (filters.vendor) params.append('vendor', filters.vendor);
  if (filters.tactic) params.append('tactic', filters.tactic);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await axios.get(`${API_URL}?${params.toString()}`);
  return response.data;
};

// Get remediations for a specific technique
const getByTechnique = async (techniqueId) => {
  const response = await axios.get(`${API_URL}/technique/${techniqueId}`);
  return response.data;
};

// Get remediations for a specific vendor
const getByVendor = async (vendor) => {
  const response = await axios.get(`${API_URL}/vendor/${vendor}`);
  return response.data;
};

// Get supported vendors
const getVendors = async () => {
  const response = await axios.get(`${API_URL}/vendors`);
  return response.data;
};

// Get coverage statistics
const getStats = async () => {
  const response = await axios.get(`${API_URL}/stats`);
  return response.data;
};

// Get single remediation by ID
const getRemediation = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Create new remediation (Admin)
const createRemediation = async (remediationData) => {
  const response = await axios.post(API_URL, remediationData);
  return response.data;
};

// Update remediation (Admin)
const updateRemediation = async (id, remediationData) => {
  const response = await axios.put(`${API_URL}/${id}`, remediationData);
  return response.data;
};

// Delete remediation (Admin)
const deleteRemediation = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Add feedback/rating
const addFeedback = async (id, rating, comment) => {
  const response = await axios.post(`${API_URL}/${id}/feedback`, { rating, comment });
  return response.data;
};

// Bulk import remediations (Admin)
const bulkImport = async (remediations) => {
  const response = await axios.post(`${API_URL}/bulk-import`, { remediations });
  return response.data;
};

// Seed initial data (Admin)
const seedData = async () => {
  const response = await axios.post(`${API_URL}/seed`);
  return response.data;
};

// Helper: Get vendor display info
const getVendorInfo = (vendorId) => {
  const vendors = {
    crowdstrike: { name: 'CrowdStrike Falcon', color: '#E83A30', icon: 'falcon' },
    microsoft_defender: { name: 'Microsoft Defender', color: '#0078D4', icon: 'microsoft' },
    microsoft_sentinel: { name: 'Microsoft Sentinel', color: '#0078D4', icon: 'microsoft' },
    splunk: { name: 'Splunk', color: '#65A637', icon: 'chart-line' },
    elastic: { name: 'Elastic Security', color: '#00BFB3', icon: 'search' },
    palo_alto: { name: 'Palo Alto Cortex XDR', color: '#FA582D', icon: 'shield-alt' },
    carbon_black: { name: 'VMware Carbon Black', color: '#1DA462', icon: 'shield-check' },
    sentinelone: { name: 'SentinelOne', color: '#6C2DC7', icon: 'robot' },
    tanium: { name: 'Tanium', color: '#D8232A', icon: 'network-wired' },
    qualys: { name: 'Qualys', color: '#ED1C24', icon: 'bug' },
    tenable: { name: 'Tenable.io', color: '#0096D6', icon: 'search-plus' },
    rapid7: { name: 'Rapid7 InsightIDR', color: '#E8740C', icon: 'bolt' },
    generic: { name: 'Generic Guidance', color: '#6B7280', icon: 'book' }
  };
  return vendors[vendorId] || { name: vendorId, color: '#6B7280', icon: 'question' };
};

// Helper: Get query language info
const getQueryLanguageInfo = (lang) => {
  const languages = {
    kql: { name: 'KQL (Kusto)', description: 'Microsoft query language' },
    spl: { name: 'SPL', description: 'Splunk Processing Language' },
    eql: { name: 'EQL', description: 'Event Query Language (Elastic)' },
    lucene: { name: 'Lucene', description: 'Lucene query syntax' },
    sql: { name: 'SQL', description: 'Structured Query Language' },
    other: { name: 'Other', description: 'Custom query language' }
  };
  return languages[lang] || { name: lang, description: '' };
};

const remediationService = {
  getRemediations,
  getByTechnique,
  getByVendor,
  getVendors,
  getStats,
  getRemediation,
  createRemediation,
  updateRemediation,
  deleteRemediation,
  addFeedback,
  bulkImport,
  seedData,
  getVendorInfo,
  getQueryLanguageInfo
};

export default remediationService;
